/**
 * Document Progress Service
 * Handles real-time progress updates for document chunking operations
 */

class DocumentProgressService {
  constructor() {
    this.listeners = [];
    this.isPolling = false;
    this.pollInterval = null;
    this.currentJobId = null;
  }

  /**
   * Add a progress listener
   * @param {Function} callback - Function to call when progress updates
   * @returns {Function} Unsubscribe function
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Broadcast progress to all listeners
   * @param {Object} progressData - Progress data to broadcast
   */
  broadcast(progressData) {
    this.listeners.forEach(callback => {
      try {
        callback(progressData);
      } catch (error) {
        console.error('Progress broadcast error:', error);
      }
    });
  }

  /**
   * Start polling for progress updates
   * @param {string} jobId - Job ID to track
   * @param {number} interval - Polling interval in milliseconds (default: 2000)
   */
  startPolling(jobId, interval = 2000) {
    this.currentJobId = jobId;
    this.isPolling = true;

    // Clear any existing interval
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }

    // Start with immediate poll
    this.pollProgress();

    // Set up polling interval
    this.pollInterval = setInterval(() => {
      if (this.isPolling) {
        this.pollProgress();
      }
    }, interval);
  }

  /**
   * Stop polling for progress updates
   */
  stopPolling() {
    this.isPolling = false;
    this.currentJobId = null;
    
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }

    // Broadcast that processing is complete/stopped
    this.broadcast({
      isVisible: false,
      currentStage: 'Complete',
      stageProgress: 100,
      overallProgress: 100,
      documentTitle: ''
    });
  }

  /**
   * Poll for current progress
   */
  async pollProgress() {
    if (!this.currentJobId) return;

    try {
      // In a real implementation, this would call your backend API
      // For now, we'll use a mock implementation
      const response = await fetch(`/api/chunking-progress/${this.currentJobId}`);
      
      if (response.ok) {
        const progressData = await response.json();
        this.broadcast({
          isVisible: progressData.isProcessing,
          currentStage: progressData.currentStage,
          stageProgress: progressData.stageProgress,
          overallProgress: progressData.overallProgress,
          documentTitle: progressData.documentTitle
        });

        // Stop polling if processing is complete
        if (!progressData.isProcessing) {
          this.stopPolling();
        }
      } else {
        console.warn('Failed to fetch progress:', response.status);
      }
    } catch (error) {
      console.error('Progress polling error:', error);
      // Continue polling even on errors, but broadcast error state
      this.broadcast({
        isVisible: false,
        currentStage: 'Error',
        stageProgress: 0,
        overallProgress: 0,
        documentTitle: ''
      });
    }
  }

  /**
   * Simulate progress updates (for testing/demo purposes)
   * @param {string} documentTitle - Title of the document being processed
   */
  simulateProgress(documentTitle = 'Test Document') {
    const stages = [
      'Processing Document',
      'Breaking it Down',
      'Understanding the Content',
      'Organizing for Search'
    ];

    const stageWeights = {
      'Processing Document': 0.1,
      'Breaking it Down': 0.1,
      'Understanding the Content': 0.6,
      'Organizing for Search': 0.2
    };

    let currentStageIndex = 0;
    let stageProgress = 0;

    // Start processing
    this.broadcast({
      isVisible: true,
      currentStage: stages[0],
      stageProgress: 0,
      overallProgress: 0,
      documentTitle
    });

    const simulationInterval = setInterval(() => {
      // Increment stage progress
      stageProgress += Math.random() * 15 + 5; // 5-20% increment

      if (stageProgress >= 100) {
        stageProgress = 100;
        
        // Calculate overall progress
        let overallProgress = 0;
        for (let i = 0; i < stages.length; i++) {
          const weight = stageWeights[stages[i]] || 0;
          if (i < currentStageIndex) {
            overallProgress += weight * 100; // Completed stages
          } else if (i === currentStageIndex) {
            overallProgress += weight * stageProgress; // Current stage
          }
        }

        this.broadcast({
          isVisible: true,
          currentStage: stages[currentStageIndex],
          stageProgress,
          overallProgress: Math.min(overallProgress, 100),
          documentTitle
        });

        // Move to next stage
        setTimeout(() => {
          currentStageIndex++;
          if (currentStageIndex >= stages.length) {
            // Processing complete
            this.broadcast({
              isVisible: false,
              currentStage: 'Complete',
              stageProgress: 100,
              overallProgress: 100,
              documentTitle
            });
            clearInterval(simulationInterval);
          } else {
            stageProgress = 0;
          }
        }, 500);
      } else {
        // Calculate overall progress
        let overallProgress = 0;
        for (let i = 0; i < stages.length; i++) {
          const weight = stageWeights[stages[i]] || 0;
          if (i < currentStageIndex) {
            overallProgress += weight * 100; // Completed stages
          } else if (i === currentStageIndex) {
            overallProgress += weight * stageProgress; // Current stage
          }
        }

        this.broadcast({
          isVisible: true,
          currentStage: stages[currentStageIndex],
          stageProgress,
          overallProgress: Math.min(overallProgress, 100),
          documentTitle
        });
      }
    }, 1000 + Math.random() * 1000); // Variable timing for realism
  }
}

// Export singleton instance
export default new DocumentProgressService();